import { useQuery, UseQueryResult } from 'react-query';

import { getTokenWithType } from './useAuth';

import { getAccountInfo } from '../utils/urls';

import { AccountFormInputs, AccountInfoReturn } from 'types/accounts';
import { ErrorModal } from 'types/modalTypes';

async function fetchAccountInfo({ id }: TVariables): Promise<APIResult> {
  if (!id)
    return new Promise((resolve, reject) => {
      reject();
    });
  const response = await fetch(getAccountInfo(id), {
    method: 'GET',
    headers: {
      Authorization: getTokenWithType(),
    },
  });
  return response.json();
}

type TError = ErrorModal;

type APIResult = {
  Data: AccountInfoReturn;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult =
  | {
      accountInfoReturn: {
        Data: AccountInfoReturn;
        IsSuccess: boolean;
        StatusCode: number;
      };

      accountFormInputs: AccountFormInputs;
    }
  | undefined;

type TVariables = {
  id: number | string | null;
};

function useGetAccountInfo(
  id?: number | string,
): UseQueryResult<TResult, TError> {
  return useQuery(
    ['get_account', id],
    async () => {
      if (!id) return;
      const fetchResponse = await fetchAccountInfo({ id });
      const result: AccountFormInputs = {
        AccountServiceId: fetchResponse.Data.AccountServiceId,
        AnswerScript: fetchResponse.Data.AnswerScript,
        AnswerScriptDetailType: fetchResponse.Data.AnswerScriptDetailType,
        AnswerScriptDetails: fetchResponse.Data.AnswerScriptDetails,
        AutoPurgeDeadlineInHours: fetchResponse.Data.AutoPurgeDeadlineInHours,
        CID: fetchResponse.Data.CID,
        City: fetchResponse.Data.City,
        Id: fetchResponse.Data.Id,
        IsAutoPurgeEnabled: fetchResponse.Data.IsAutoPurgeEnabled,
        IsLive: fetchResponse.Data.IsLive,
        IsPriorityFinalization: fetchResponse.Data.IsPriorityFinalization,
        Name: fetchResponse.Data.Name,
        PID: fetchResponse.Data.PID,
        ReportingDisplayName: fetchResponse.Data.ReportingDisplayName,
        SID: fetchResponse.Data.SID,
        VDN: fetchResponse.Data.VDN,
        StateProvinceId: fetchResponse.Data.StateProvinceId,
        StatusId: fetchResponse.Data.StatusId,
        TimeZoneId: fetchResponse.Data.TimeZoneId,
        RelationshipManagerId: fetchResponse.Data.RelationshipManagerId,
        PersonAlertLimiter:
          fetchResponse.Data.LimitCategory == null ? false : true,
        LimitCategory: fetchResponse.Data.LimitCategory ?? '',
        AccountCategoryLimitingValues:
          fetchResponse.Data.LimitCategory == null ||
          fetchResponse.Data?.AccountCategoryLimitingValues?.length == 0
            ? [
                {
                  Id: 0,
                  Name: '',
                  AccountId: null,
                },
              ]
            : fetchResponse.Data.AccountCategoryLimitingValues,
      };
      return { accountFormInputs: result, accountInfoReturn: fetchResponse };
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetAccountInfo };
